/**
 * Detail (docs/11 §3.5) — the one screen a driver reads while parked. Opens
 * instantly from the row's data; the occurrence fetch only enriches. Every
 * number carries its basis in words (docs/10 principle 3); confidence is
 * never a percentage. Primary action sits in the thumb zone.
 */
import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Check, MapPin, Flag, Info, CarFront, CalendarDays } from 'lucide-react';
import { api, type Detail, type Opp } from './api.js';
import { Button, InlineWarn } from './ui.js';
import { Countdown } from './EventCard.js';
import { CrowdMeter } from './CrowdMeter.js';
import { LiveNow, hasLiveNow } from './LiveNow.js';
import {
  fmtWindow, fmtTime, etaLabel, headingLabel, categoryLabel, fmtDistance, basisText,
  REASON_TEXT, dayPrefix, fmtPastDay, pastTitle, fmtEventWhen, sourceParts,
} from './format.js';

const MODE_LABEL: Record<string, string> = {
  taxi: 'taxis', rideshare: 'rideshare', livery: 'livery', private_hire: 'private hire',
};

export function DetailScreen({ opp, now, units, hasOrigin, driverMode, tripActiveTitle, busy, error, onBack, onHeadThere }: {
  opp: Opp;
  now: number;
  units: 'mi' | 'km';
  /** False when the driver's position is unknown: no timing claims. */
  hasOrigin: boolean;
  driverMode: string;
  /** Title of the active trip, if any: Head there becomes Switch to this. */
  tripActiveTitle: string | null;
  busy: boolean;
  error: string | null;
  onBack: () => void;
  onHeadThere: () => void;
}) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [reported, setReported] = useState(false);
  const venue = opp.occurrence.venue_name ?? opp.occurrence.title;
  const day = dayPrefix(opp.egress.p10_at, now);

  // A detail opened from far down the timeline must start at its own top.
  useEffect(() => { window.scrollTo(0, 0); }, [opp.occurrence.id]);

  useEffect(() => {
    let alive = true;
    api.occurrence(opp.occurrence.id)
      .then((d) => { if (alive) setDetail(d); })
      .catch(() => { /* the row's data is enough to render */ });
    return () => { alive = false; };
  }, [opp.occurrence.id]);

  async function reportExiting() {
    try { await api.crowdExiting(opp.occurrence.id); } catch { /* rate-limited: fine */ }
    setReported(true);
  }

  const zones = detail?.staging_zones ?? [{
    zone_id: opp.staging.zone_id, label: opp.staging.label, approach: opp.staging.approach,
    accessibility: null, disclaimer: opp.staging.disclaimer, permitted_modes: [],
    lat: opp.staging.lat, lng: opp.staging.lng,
  }];
  // Reporting only makes sense around the exit itself; days ahead the button
  // is noise and a stray tap would mislead other drivers.
  const canReport = now >= new Date(opp.egress.p10_at).getTime() - 45 * 60_000
    && now <= new Date(opp.egress.p90_at).getTime() + 90 * 60_000;
  const past = detail?.past ?? null;
  const src = sourceParts(opp);
  const pct = (n: number) => (detail?.capacity ? Math.round((n / detail.capacity) * 100) : null);

  return (
    <div className="screen">
      <header className="hdr">
        <button className="back" onClick={onBack}><ChevronLeft size={24} aria-hidden="true" /> Tonight</button>
      </header>
      <h1 className="title">{venue}</h1>
      {opp.occurrence.venue_name && (
        <p className="sub">{opp.occurrence.title} · {categoryLabel(opp.occurrence.category)}</p>
      )}

      <div className="meta num">
        <span className="pair wrap"><CalendarDays size={18} aria-hidden="true" /><span className="wrap" style={{ color: 'var(--text-1)', fontWeight: 600 }}>{fmtEventWhen(opp.occurrence.starts_at, now)}</span></span>
      </div>

      <section className="card">
        {opp.leave_by ? (
          <>
            <span className="label">Leave in</span>
            <Countdown target={opp.leave_by} now={now} size="hero" />
            <span className="sub num">by {fmtTime(opp.leave_by)}</span>
          </>
        ) : (
          <span className="sub">{!hasOrigin ? 'Turn on location for a leave-by time' : 'Too far to make this one in time'}</span>
        )}
        <div className="meta num" style={{ marginTop: 6 }}>
          <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>
            Crowd out {fmtWindow(opp.egress.p10_at, opp.egress.p90_at)}{day ? ` · ${day}` : ''}
          </span>
        </div>
        <div className="meta small">
          <span className="pair wrap"><Info size={16} aria-hidden="true" /><span className="wrap">Most likely {fmtTime(opp.egress.p50_at)} · {basisText(opp)}</span></span>
        </div>
        <div className="meta small">
          <span>{hasOrigin ? `${etaLabel(opp.eta_band)} · ` : ''}{fmtDistance(opp.distance_km, units)} from where you’re looking</span>
        </div>
        {hasOrigin && opp.eta_band !== 'unknown' && (
          <p className="small muted">Drive time estimated from distance — live traffic isn’t connected yet.</p>
        )}
      </section>

      {hasLiveNow(opp) && (
        <section>
          <h2 className="heading" style={{ marginBottom: 8 }}>Right now</h2>
          <LiveNow opp={opp} now={now} />
        </section>
      )}

      <section className="card" aria-label="Crowd size">
        <CrowdMeter opp={opp} size="lg" />
        <div className="meta small" style={{ justifyContent: 'center', textAlign: 'center' }}>
          <span className="wrap">
            {past
              ? `From what the last ${past.count} events like this drew here`
              : opp.crowd.basis === 'past_events'
                ? 'From what past events like this drew here'
                : 'An estimate — no past attendance on record here yet'}
          </span>
        </div>
      </section>

      {past && (
        <section>
          <h2 className="heading" style={{ marginBottom: 4 }}>What past events drew</h2>
          <p className="sub num" style={{ marginBottom: 10 }}>
            {past.low === past.high
              ? `${past.typical.toLocaleString()} every time`
              : `${past.low.toLocaleString()} to ${past.high.toLocaleString()} · usually ${past.typical.toLocaleString()}`}
            {pct(past.typical) !== null ? ` (${pct(past.typical)}% full)` : ''}
          </p>
          <ul className="past">
            {past.events.map((e) => (
              <li key={e.starts_at + e.title}>
                <span className="who">{pastTitle(e.title)}</span>
                <span className="drew">{e.attendance.toLocaleString()}</span>
                <span className="date">{fmtPastDay(e.starts_at)}</span>
                <span className="pct">{e.fill !== null ? `${Math.round(e.fill * 100)}% full` : ''}</span>
                {e.fill !== null && (
                  <span className="bar" aria-hidden="true"><i style={{ width: `${Math.min(100, Math.round(e.fill * 100))}%` }} /></span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2 className="heading" style={{ marginBottom: 8 }}>Why this one</h2>
        <ul className="why">
          {opp.reason_codes.filter((rc) => REASON_TEXT[rc]).map((rc) => (
            <li key={rc}><Check size={18} aria-hidden="true" /><span>{REASON_TEXT[rc]!(opp.heading)}</span></li>
          ))}
          <li><CarFront size={18} aria-hidden="true" /><span>{headingLabel(opp.heading)}</span></li>
        </ul>
      </section>

      <section>
        <h2 className="heading" style={{ marginBottom: 8 }}>Pickup for {MODE_LABEL[driverMode] ?? driverMode}</h2>
        {zones.map((z, i) => (
          <div key={z.zone_id} className="inset" style={{ marginBottom: 8 }}>
            <MapPin size={18} aria-hidden="true" />
            <div className="zone">
              <b>{z.label}</b>
              {z.approach && <span>{z.approach}</span>}
              {z.accessibility && <span>{z.accessibility}</span>}
              {i === 0 && <span className="disclaimer">{z.disclaimer}</span>}
            </div>
          </div>
        ))}
      </section>

      <section>
        <h2 className="heading" style={{ marginBottom: 8 }}>Where this comes from</h2>
        <dl className="srcs">
          <dt>Event and start time</dt><dd>{src.event}</dd>
          <dt>Crowd size</dt><dd>{src.crowd}{past ? ` (last ${past.count} events)` : ''}</dd>
          <dt>Exit time</dt><dd>{src.exit}</dd>
          {opp.tickets?.resale_left != null && (<><dt>Resale tickets</dt><dd>Vivid Seats listings, via ESPN</dd></>)}
          {opp.tickets?.sales && (<><dt>Ticket availability</dt><dd>Ticketmaster</dd></>)}
          {opp.busyness && (<><dt>Venue busyness</dt><dd>BestTime foot-traffic data</dd></>)}
          {opp.live && (<><dt>Live game</dt><dd>ESPN game data</dd></>)}
        </dl>
      </section>

      <div className="sticky-cta">
        {error && <InlineWarn danger>{error}</InlineWarn>}
        {tripActiveTitle && !error && (
          <p className="inline-note" style={{ marginBottom: 8 }}>You’re heading to {tripActiveTitle}. This will replace it.</p>
        )}
        <Button variant="primary" busy={busy} onClick={onHeadThere} icon={<ChevronRight size={20} aria-hidden="true" />}>
          {tripActiveTitle ? 'Switch to this' : 'Head there'}
        </Button>
        {canReport && (
          <Button variant="text" onClick={() => void reportExiting()} disabled={reported}
            icon={<Flag size={18} aria-hidden="true" />} style={{ marginTop: 6 }}>
            {reported ? 'Thanks — other drivers can see it' : 'Crowd is coming out now'}
          </Button>
        )}
      </div>
    </div>
  );
}
